import { StatusBar } from "expo-status-bar";
import {
  Platform,
  StyleSheet,
  ScrollView,
  Text,
  TextInput,
  Pressable,
  useColorScheme,
  Alert,
} from "react-native";
import AsyncStorage from "@react-native-async-storage/async-storage";
import { useEffect, useState } from "react";
import { View } from "../components/Themed";
import { useQuery, useMutation, gql } from "@apollo/client";
import { Picker } from "@react-native-picker/picker";
import Config from "../constants/Config";
import { v4 as uuidv4 } from "uuid";

const ROLE_OPTIONS = [
  { label: "Operator", value: "operator" },
  { label: "Kiosk", value: "kiosk" },
  { label: "Manager", value: "manager" },
  { label: "Admin", value: "admin" },
];

function formatRole(role) {
  const found = ROLE_OPTIONS.find((entry) => entry.value === role);
  if (found) {
    return found.label;
  }
  return role || "Operator";
}

const OPERATORS_QUERY = gql`
  query getOperators($token: String!) {
    operators(token: $token) {
      id
      name
      role
      defaultLocation
    }
  }
`;

const LOCATIONS_QUERY = gql`
  query getLocations($token: String!) {
    locations(token: $token) {
      id
      name
      city
    }
  }
`;

const CREATE_OPERATOR = gql`
  mutation createOperator(
    $token: String!
    $operator: ID!
    $id: ID!
    $name: String!
    $role: String!
    $defaultLocation: ID
  ) {
    createOperator(
      token: $token
      operator: $operator
      id: $id
      name: $name
      role: $role
      defaultLocation: $defaultLocation
    ) {
      id
      name
      role
      defaultLocation
    }
  }
`;

const UPDATE_OPERATOR = gql`
  mutation updateOperator(
    $token: String!
    $operator: ID!
    $id: ID!
    $name: String
    $role: String
    $defaultLocation: ID
  ) {
    updateOperator(
      token: $token
      operator: $operator
      id: $id
      name: $name
      role: $role
      defaultLocation: $defaultLocation
    ) {
      id
      name
      role
      defaultLocation
    }
  }
`;

export default function OperatorSettingsOperatorsScreen() {
  const [operatorId, setOperatorId] = useState("");
  const [token, setToken] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [editingOperator, setEditingOperator] = useState(null);
  const [name, setName] = useState("");
  const [role, setRole] = useState("operator");
  const [defaultLocation, setDefaultLocation] = useState("");
  const colorScheme = useColorScheme();

  useEffect(() => {
    async function fetchData() {
      try {
        const storedId = await AsyncStorage.getItem("operatorId");
        setOperatorId(storedId);
      } catch (ex) {}

      try {
        const storedToken = await AsyncStorage.getItem("token");
        setToken(storedToken);
      } catch (ex) {}
    }
    fetchData();
  }, []);

  const { loading, error, data, refetch } = useQuery(OPERATORS_QUERY, {
    variables: { token },
    skip: !token,
  });

  const { data: locationsData } = useQuery(LOCATIONS_QUERY, {
    variables: { token },
    skip: !token,
  });

  const [createOperator] = useMutation(CREATE_OPERATOR);
  const [updateOperator] = useMutation(UPDATE_OPERATOR);

  const resetForm = () => {
    setName("");
    setRole("operator");
    setDefaultLocation("");
    setEditingOperator(null);
    setShowForm(false);
  };

  const handleSubmit = async () => {
    if (!name.trim()) {
      Alert.alert("Error", "Name is required");
      return;
    }

    try {
      if (editingOperator) {
        await updateOperator({
          variables: {
            token,
            operator: operatorId,
            id: editingOperator.id,
            name: name.trim(),
            role,
            defaultLocation: defaultLocation || null,
          },
        });
      } else {
        await createOperator({
          variables: {
            token,
            operator: operatorId,
            id: uuidv4(),
            name: name.trim(),
            role,
            defaultLocation: defaultLocation || null,
          },
        });
      }
      resetForm();
      refetch();
    } catch (err) {
      Alert.alert("Error", err.message || "Failed to save operator");
    }
  };

  const handleEdit = (op) => {
    setEditingOperator(op);
    setName(op.name);
    setRole(op.role || "operator");
    setDefaultLocation(op.defaultLocation || "");
    setShowForm(true);
  };

  const textColor = colorScheme === "dark" ? "white" : "black";
  const bgColor = colorScheme === "dark" ? "#333" : "white";

  return (
    <ScrollView style={{ flex: 1 }}>
      <View style={styles.container}>
        {!showForm ? (
          <>
            <Pressable
              style={[styles.addButton, { backgroundColor: Config.companyColor || "#007AFF" }]}
              onPress={() => setShowForm(true)}
            >
              <Text style={styles.addButtonText}>+ Add Operator</Text>
            </Pressable>

            {loading && <Text style={{ color: textColor, padding: 20 }}>Loading...</Text>}
            {error && <Text style={{ color: "red", padding: 20 }}>Error: {error.message}</Text>}

            {data?.operators?.map((op) => (
              <Pressable
                key={op.id}
                style={[styles.operatorItem, { backgroundColor: bgColor }]}
                onPress={() => handleEdit(op)}
              >
                <View style={{ backgroundColor: "transparent", flex: 1 }}>
                  <Text style={[styles.operatorName, { color: textColor }]}>{op.name}</Text>
                  <Text style={[styles.operatorRole, { color: textColor, opacity: 0.7 }]}>
                    {formatRole(op.role)}
                  </Text>
                </View>
                <Text style={{ color: textColor, opacity: 0.5 }}>Edit</Text>
              </Pressable>
            ))}
          </>
        ) : (
          <View style={[styles.form, { backgroundColor: bgColor }]}>
            <Text style={[styles.formTitle, { color: textColor }]}>
              {editingOperator ? "Edit Operator" : "New Operator"}
            </Text>

            <Text style={[styles.label, { color: textColor }]}>Name</Text>
            <TextInput
              value={name}
              onChangeText={setName}
              placeholder="Operator name"
              placeholderTextColor={colorScheme === "dark" ? "#888" : "#999"}
              style={[
                styles.input,
                {
                  color: textColor,
                  backgroundColor: colorScheme === "dark" ? "#444" : "#f5f5f5",
                },
              ]}
            />

            <Text style={[styles.label, { color: textColor }]}>Role</Text>
            <View style={[styles.pickerContainer, { backgroundColor: colorScheme === "dark" ? "#444" : "#f5f5f5" }]}>
              <Picker
                selectedValue={role}
                onValueChange={setRole}
                style={{ color: textColor }}
              >
                {ROLE_OPTIONS.map((entry) => (
                  <Picker.Item key={entry.value} label={entry.label} value={entry.value} />
                ))}
              </Picker>
            </View>

            <Text style={[styles.label, { color: textColor }]}>Default Location</Text>
            <View style={[styles.pickerContainer, { backgroundColor: colorScheme === "dark" ? "#444" : "#f5f5f5" }]}>
              <Picker
                selectedValue={defaultLocation}
                onValueChange={setDefaultLocation}
                style={{ color: textColor }}
              >
                <Picker.Item label="None" value="" />
                {locationsData?.locations?.map((loc) => (
                  <Picker.Item
                    key={loc.id}
                    label={`${loc.name} - ${loc.city}`}
                    value={loc.id}
                  />
                ))}
              </Picker>
            </View>

            <View style={styles.buttonRow}>
              <Pressable
                style={[styles.button, styles.cancelButton]}
                onPress={resetForm}
              >
                <Text style={styles.cancelButtonText}>Cancel</Text>
              </Pressable>
              <Pressable
                style={[styles.button, styles.saveButton, { backgroundColor: Config.companyColor || "#007AFF" }]}
                onPress={handleSubmit}
              >
                <Text style={styles.saveButtonText}>
                  {editingOperator ? "Update" : "Create"}
                </Text>
              </Pressable>
            </View>
          </View>
        )}

        <StatusBar style={Platform.OS === "ios" ? "light" : "auto"} />
      </View>
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: 20,
  },
  addButton: {
    padding: 15,
    borderRadius: 8,
    alignItems: "center",
    marginBottom: 20,
  },
  addButtonText: {
    color: "white",
    fontSize: 16,
    fontWeight: "bold",
  },
  operatorItem: {
    flexDirection: "row",
    alignItems: "center",
    padding: 15,
    marginBottom: 10,
    borderRadius: 8,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.1,
    shadowRadius: 2,
    elevation: 2,
  },
  operatorName: {
    fontSize: 16,
    fontWeight: "600",
  },
  operatorRole: {
    fontSize: 14,
    marginTop: 2,
  },
  form: {
    padding: 20,
    borderRadius: 12,
  },
  formTitle: {
    fontSize: 20,
    fontWeight: "bold",
    marginBottom: 20,
  },
  label: {
    fontSize: 14,
    fontWeight: "600",
    marginBottom: 5,
    marginTop: 15,
  },
  input: {
    padding: 12,
    borderRadius: 8,
    fontSize: 16,
  },
  pickerContainer: {
    borderRadius: 8,
    overflow: "hidden",
  },
  buttonRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginTop: 30,
    backgroundColor: "transparent",
  },
  button: {
    flex: 1,
    padding: 15,
    borderRadius: 8,
    alignItems: "center",
  },
  cancelButton: {
    backgroundColor: "#ddd",
    marginRight: 10,
  },
  cancelButtonText: {
    color: "#333",
    fontSize: 16,
    fontWeight: "600",
  },
  saveButton: {
    marginLeft: 10,
  },
  saveButtonText: {
    color: "white",
    fontSize: 16,
    fontWeight: "600",
  },
});
